import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Input } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { RenderModalFunction, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { H1, Text } from "@amzn/stencil-react-components/text";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useState } from "react";

import { ColorPicker, GradientValue } from "./components/ColorPicker";

interface EditSpaceModalProps {
  trigger: (open: () => void) => React.ReactNode;
  title: string;
  description: string;
  gradient: GradientValue;
  onSave: (data: { title: string; description: string; gradient: GradientValue }) => void;
}

export const EditSpaceModal: FC<EditSpaceModalProps> = ({
  trigger,
  title: initialTitle,
  description: initialDesc,
  gradient: initialGradient,
  onSave,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDesc);
  const [gradient, setGradient] = useState<GradientValue>(initialGradient);

  const renderTitleField = () => (
    <Col gridGap="dimensions.spacing.100">
      <Text fontWeight="bold">{getString("spaces.create.pageTitle")} *</Text>
      <Input value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} />
    </Col>
  );

  const renderDescriptionField = () => (
    <Col gridGap="dimensions.spacing.100">
      <Text fontWeight="bold">{getString("spaces.create.description")} *</Text>
      <TextArea
        value={description}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
        touchpoint="edit-space-description"
        style={{ minHeight: 120 }}
      />
      <Text color="color.text.secondary" fontSize="T100">
        {getString("spaces.create.descriptionHint")}
      </Text>
    </Col>
  );

  const renderGradientField = () => (
    <Col gridGap="dimensions.spacing.100">
      <Text fontWeight="bold">{getString("spaces.edit.headerGradient")}</Text>
      <ColorPicker gradient={gradient} onGradientChange={setGradient} />
    </Col>
  );

  const renderActions = (close: () => void) => (
    <Row gridGap="dimensions.spacing.200" justifyContent="flex-end" margin={{ top: "dimensions.spacing.200" }}>
      <Button variant={ButtonVariant.Secondary} onClick={close}>
        {getString("spaces.create.cancel")}
      </Button>
      <Button
        variant={ButtonVariant.Primary}
        onClick={() => {
          onSave({ title, description, gradient });
          close();
        }}
        disabled={!title.trim() || !description.trim()}
      >
        {getString("spaces.create.submit")}
      </Button>
    </Row>
  );

  const renderModal: RenderModalFunction = ({ close }: RenderModalFunctionParams) => (
    <Col padding="dimensions.spacing.500" gridGap="dimensions.spacing.400" style={{ maxWidth: 640 }}>
      <H1>{getString("spaces.page.editSettings")}</H1>
      {renderTitleField()}
      {renderDescriptionField()}
      {renderGradientField()}
      <Text color="color.text.secondary" fontSize="T100">
        {getString("spaces.create.visibilityNote")}
      </Text>
      {renderActions(close)}
    </Col>
  );

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <>
          {trigger(() => {
            setTitle(initialTitle);
            setDescription(initialDesc);
            setGradient(initialGradient);
            open();
          })}
        </>
      )}
    </WithModal>
  );
};
